import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, type ChatMessage } from "@/lib/api";

interface Source { id: string; name: string; short_title?: string | null; }
interface Bubble { role: "user" | "ai"; text: string; error?: boolean; }

interface Props {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** External prompt hand-off — when set, dock opens + auto-submits. */
  pendingPrompt: string | null;
  onPromptConsumed: () => void;
  onOpenScheme: (id: string) => void;
}

const SUGGESTIONS = [
  "I'm a first-time founder in Karnataka, what do I qualify for?",
  "Compare SISFS vs Atal Innovation Mission seed grants.",
  "I run a small textile unit. Which Mudra tier, CGTMSE setup and ZED grant apply?",
  "I'm a woman entrepreneur — what should I prioritise?",
];

export function ChatDock({ open, onOpen, onClose, pendingPrompt, onPromptConsumed, onOpenScheme }: Props) {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [input, setInput]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [compact, setCompact] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setBubbles([]);
    setHistory([]);
    setSources([]);
  };

  const greetIfEmpty = () => {
    if (bubbles.length) return;
    setBubbles([
      {
        role: "ai",
        text: "Hi — I'm an AI search over Indian govt scheme pages.\n\n" +
              "Tell me your stage, state or need and I'll surface the schemes that fit. " +
              "I'm not affiliated with any govt body — always verify on the official portal before applying.\n\n" +
              "Examples you can tap:",
      },
    ]);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpen]);

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) greetIfEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [bubbles, sources]);

  useEffect(() => {
    if (pendingPrompt && open) {
      setInput(pendingPrompt);
      onPromptConsumed();
      // Queue a microtask so input state actually flushes first.
      Promise.resolve().then(() => void sendMessage(pendingPrompt));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt, open]);

  const sendMessage = async (text?: string) => {
    const body = (text ?? input).trim();
    if (!body || busy) return;
    setInput("");
    setBusy(true);

    const newHistory = [...history, { role: "user" as const, content: body }];
    setHistory(newHistory);
    setBubbles((b) => [...b, { role: "user", text: body }, { role: "ai", text: "" }]);

    try {
      const resp = await api.chatStream(newHistory.slice(-10));
      if (!resp.ok || !resp.body) {
        const raw = await resp.text();
        updateLastAi(() => `Error: ${raw.slice(0, 200)}`, true);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(/\n\n/);
        buffer = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n");
          const type = (lines.find((l) => l.startsWith("event:")) || "").slice(6).trim();
          const data = (lines.find((l) => l.startsWith("data:")) || "").slice(5).trim();
          if (!type || !data) continue;
          let payload: any;
          try { payload = JSON.parse(data); } catch { continue; }
          if (type === "sources") setSources(payload);
          else if (type === "token") {
            full += String(payload);
            updateLastAi(() => full);
          } else if (type === "error") {
            updateLastAi(() => `Error: ${payload?.message || "unknown"}`, true);
          } else if (type === "done") {
            setHistory((h) => [...h, { role: "assistant", content: full }]);
          }
        }
      }
      if (!full) updateLastAi(() => "(no response — try again)", true);
    } catch (e) {
      updateLastAi(() => `Network error: ${e instanceof Error ? e.message : e}`, true);
    } finally {
      setBusy(false);
    }
  };

  const updateLastAi = (fn: (prev: string) => string, error = false) => {
    setBubbles((b) => {
      const next = b.slice();
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === "ai") {
          next[i] = { ...next[i], text: fn(next[i].text), error };
          break;
        }
      }
      return next;
    });
  };

  return (
    <>
      <button
        onClick={onOpen}
        aria-label="Ask Ākāśavāṇī"
        className={`fixed bottom-6 right-6 z-30 flex items-center gap-2
                    bg-gradient-to-br from-amber-400 to-coral-500 text-plum-950
                    rounded-full shadow-[0_18px_45px_-10px_rgba(239,179,90,0.45)]
                    hover:from-amber-300 hover:to-amber-500 transition
                    ${compact ? "px-3 py-3" : "px-4 py-3"}`}
      >
        <span className="relative flex w-3 h-3">
          <span className="absolute inline-flex w-full h-full rounded-full bg-cream-50 animate-pulse-ring" />
          <span className="relative inline-flex rounded-full w-3 h-3 bg-cream-50" />
        </span>
        <span
          className="font-semibold whitespace-nowrap transition-[max-width,opacity,margin] duration-200 overflow-hidden font-display"
          style={{
            maxWidth: compact ? 0 : 180,
            opacity:  compact ? 0 : 1,
            marginLeft: compact ? 0 : undefined,
          }}
        >
          Ask Ākāśavāṇī
        </span>
        <kbd className="hidden md:inline font-mono text-[10px] border border-plum-900/30 rounded px-1 py-px opacity-90 !bg-transparent !text-plum-950">
          ⌘K
        </kbd>
      </button>

      <div
        className={`fixed bottom-0 right-0 md:bottom-5 md:right-5 z-40
                    w-full md:w-[520px] lg:w-[560px] h-[80vh] md:h-[calc(100vh-120px)] md:max-h-[760px]
                    bg-plum-900 md:rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]
                    border border-amber-400/25 overflow-hidden
                    flex flex-col transition-all duration-200
                    ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[calc(100%+1rem)] pointer-events-none"}`}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-amber-400/20 bg-plum-950/70">
          <div className="min-w-0">
            <div className="font-display text-base font-semibold flex items-center gap-2 text-cream-50">
              <span className="relative flex w-2.5 h-2.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-amber-400 animate-pulse-ring" />
                <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-amber-400" />
              </span>
              Ākāśavāṇī
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-cream-100/55 mt-0.5">
              grounded · cited · not govt-affiliated
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={reset} className="text-cream-100/60 hover:text-amber-300 text-[11px] font-mono uppercase tracking-[0.2em] px-2 py-1 transition">clear</button>
            <button onClick={onClose} className="text-cream-100/60 hover:text-cream-50 text-2xl leading-none px-2 transition" aria-label="Close">×</button>
          </div>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-plum-900">
          {bubbles.map((b, i) => (
            <div key={i} className={`flex ${b.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[92%] rounded-2xl px-4 py-3 text-[14px] leading-[1.55] break-words
                            ${b.role === "user"
                              ? "bg-gradient-to-br from-amber-400 to-coral-500 text-plum-950 font-medium"
                              : "bg-plum-950/60 border border-amber-400/15 text-cream-50"}`}
              >
                {b.text ? (
                  b.role === "ai" ? (
                    <div className="markdown-body space-y-2">
                      <ReactMarkdown
                        components={{
                          a: (props) => (
                            <a {...props} target="_blank" rel="noopener noreferrer"
                               className="text-amber-300 hover:text-amber-200 underline decoration-dotted underline-offset-2 break-all" />
                          ),
                          ul: (props) => <ul {...props} className="list-disc pl-5 my-1.5 space-y-1" />,
                          ol: (props) => <ol {...props} className="list-decimal pl-5 my-1.5 space-y-1" />,
                          li: (props) => <li {...props} className="leading-[1.55]" />,
                          p:  (props) => <p  {...props} className="leading-[1.6] my-1" />,
                          h1: (props) => <h3 {...props} className="font-display font-semibold text-[16px] mt-2 mb-1 text-amber-200" />,
                          h2: (props) => <h3 {...props} className="font-display font-semibold text-[15px] mt-2 mb-1 text-amber-200" />,
                          h3: (props) => <h3 {...props} className="font-display font-semibold text-[14px] mt-2 mb-1 text-amber-200" />,
                          strong: (props) => <strong {...props} className="text-amber-200 font-semibold" />,
                          code: (props) => <code {...props} className="bg-plum-950/80 text-amber-200 px-1 py-0.5 rounded text-[13px] font-mono" />,
                          pre: (props) => <pre {...props} className="bg-plum-950/80 border border-amber-400/15 p-3 rounded-lg overflow-x-auto text-[12.5px] my-2" />,
                          blockquote: (props) => <blockquote {...props} className="border-l-2 border-amber-400/50 pl-3 italic text-cream-100/80 my-1" />,
                          hr: () => <hr className="border-amber-400/15 my-3" />,
                        }}
                      >
                        {b.text}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{b.text}</div>
                  )
                ) : (
                  busy && b.role === "ai" ? (
                    <span className="inline-flex items-center gap-1.5 text-cream-100/55">
                      <span className="oracle-pulse" />
                      <span className="oracle-pulse" style={{ animationDelay: "120ms" }} />
                      <span className="oracle-pulse" style={{ animationDelay: "240ms" }} />
                    </span>
                  ) : null
                )}
              </div>
            </div>
          ))}
          {bubbles.length === 1 && (
            <div className="mt-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/70 mb-2">— cables —</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((q) => (
                  <button key={q}
                          onClick={() => sendMessage(q)}
                          className="text-left text-[12px] border border-amber-400/20 hover:border-amber-400/50 hover:text-amber-200 text-cream-100/80 rounded-full px-3 py-1.5 bg-plum-950/40 hover:bg-plum-950/70 transition">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {sources.length > 0 && (
          <div className="px-4 py-2.5 border-t border-amber-400/15 bg-plum-950/40">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-1.5">— cited —</div>
            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {sources.map((s) => (
                <button key={s.id} onClick={() => onOpenScheme(s.id)}
                        className="chip hover:bg-amber-400/20 cursor-pointer text-left">
                  {s.short_title || s.name.slice(0, 36)}
                </button>
              ))}
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="border-t border-amber-400/20 p-3 flex gap-2 bg-plum-950/50"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={busy}
            placeholder="cable ākāśavāṇī…"
            className="flex-1 bg-plum-900/60 border border-white/10 text-cream-50 placeholder:text-cream-100/40 rounded-xl px-4 py-2.5 text-[14px] outline-none focus:border-amber-400/50"
            autoComplete="off"
          />
          <button type="submit" disabled={busy}
                  className="bg-cream-50 hover:bg-amber-300 disabled:opacity-40 text-plum-950 text-[13px] font-display font-semibold px-4 rounded-xl transition">
            send
          </button>
        </form>
      </div>
    </>
  );
}
